import {
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
  Breadcrumbs,
} from '@neo/primitives/base/breadcrumbs'
import { Button } from '@neo/primitives/button'
import { CircleCheck } from 'lucide-react'
import { ChangeIn } from 'v2/pages/settings/india-kyc/change-in'
import { TONE_EASE } from 'v2/pages/settings/india-kyc/motion'

interface StepsBreadcrumbsProps {
  currentStep: number
  steps: { number: number; label: string; step?: unknown }[]
  onNavigateStep: (step: number) => void
  disabledSteps?: readonly number[]
  errorSteps?: readonly number[]
  animated?: boolean
}

// Shared and frozen so the defaults do not hand every caller a fresh array per render.
const NO_STEPS: readonly number[] = Object.freeze([])

type StepTone = 'error' | 'active' | 'inactive'

const STEP_LABEL_TONE: Record<StepTone, string> = {
  error: 'text-status-danger',
  active: 'text-p-400 text-secondary',
  inactive: 'text-tertiary',
}

const STEP_MARKER_TONE: Record<StepTone, string> = {
  error: 'border-status-danger text-status-danger',
  active: 'border-primary ',
  inactive: 'border-tertiary',
}

const toneFor = (hasError: boolean, isActive: boolean): StepTone => {
  if (hasError) return 'error'
  return isActive ? 'active' : 'inactive'
}

export const StepsBreadcrumbs = ({
  currentStep,
  steps,
  onNavigateStep,
  disabledSteps = NO_STEPS,
  errorSteps = NO_STEPS,
  animated = false,
}: StepsBreadcrumbsProps) => (
  <Breadcrumbs>
    <BreadcrumbList>
      {steps.map((step) => {
        const isDisabled = disabledSteps.includes(step.number)
        const canHover = !isDisabled
        const canClick = !isDisabled
        const hasError = errorSteps.includes(step.number)
        // The label tints once the step has been reached; the marker only fills on the
        // step being shown. Same tones, different thresholds.
        const labelTone = toneFor(hasError, currentStep >= step.number)
        const markerTone = toneFor(hasError, currentStep === step.number)
        const isDone = step.number < currentStep && !hasError
        const tones = animated ? ` ${TONE_EASE}` : ''
        const marker = isDone ? (
          <CircleCheck className="text-status-success fill-status-success/10" />
        ) : (
          <div
            className={`rounded-full border ${
              STEP_MARKER_TONE[markerTone]
            } bg-background  border-[1.5px] h-6 w-6 flex items-center justify-center${tones}`}
          >
            {step.number}
          </div>
        )

        return (
          <BreadcrumbItem key={step.number}>
            <span
              className={`flex items-center ${canHover ? 'cursor-pointer' : 'cursor-default'} ${
                STEP_LABEL_TONE[labelTone]
              }${tones}`}
              data-testid={`step-indicator-${step.number}`}
              data-invalid={hasError || undefined}
            >
              <Button
                className={`flex pl-0 gap-2 text-inherit ${
                  canHover
                    ? 'cursor-pointer hover:bg-transparent hover:text-inherit'
                    : 'cursor-default hover:bg-transparent hover:text-inherit'
                }`}
                onClick={() => canClick && onNavigateStep(step.number)}
                variant="ghost"
                type="button"
                aria-disabled={isDisabled}
                data-testid={`button-step-${step.number}`}
              >
                {animated ? (
                  <ChangeIn id={isDone ? 'done' : 'todo'} pop={isDone}>
                    {marker}
                  </ChangeIn>
                ) : (
                  marker
                )}

                {step.label}
              </Button>
              {step.number !== steps.length && (
                <BreadcrumbSeparator
                  className={`${currentStep > step.number ? 'text-secondary' : 'text-tertiary'}${tones}`}
                />
              )}
            </span>
          </BreadcrumbItem>
        )
      })}
    </BreadcrumbList>
  </Breadcrumbs>
)
